// @givmo/board — what a director is being asked to sign, and the act of signing it.
//
// ══ THE ONE THING A VOLUNTEER OPENS THIS FOR ════════════════════════════════════════════════════
//
// A board member gets an email, opens it, and needs to read a document and sign it. That is the whole
// journey, and everything here is arranged around getting them to it in as few decisions as possible:
// what is waiting for them is first, and the full text is on the page rather than behind a download.
//
// ══ THE LIST: THE OFFICER'S CARDS (CBG-UX10, D-CBG-40; frame c3) ═══════════════════════════════════
//
// "Written consents", the officer page's name, line, cards and Show chips: All, "Waiting on you" (the
// attention read's to say, as on the to-do list) and "Signed by everyone". A consent waiting on the
// director leads, with "Needs your signature" and "Read and sign" under its card; one they signed
// says so in its capture's own words. The officer's "Start one" has no counterpart.
//
// ══ SIGNING (CBG-SEC2; Jonathan's approved frames `w-*`, D-CBG-18) ════════════════════════════════
//
// Signing a consent and WITHDRAWING that signature are signature-class. The director's account
// confirms with its passkey (or, on an account with none, its authenticator app), and one
// confirmation opens signing for 60 minutes in this sign-in (D-CBG-17 decision 3), so ten consents
// in a row cost one confirmation. Each act is still its own click, below the sentence it affirms.
//
//   * Sign runs the act through the shared sheet's gate (`features/director/director-act.tsx`): an
//     open window signs at once; a shut one brings the sheet up by itself (no "Confirm to sign"
//     anywhere), then the act is sent once more with the same echo.
//   * The content address is echoed on every act because the act's value is that it names the exact
//     bytes a human read. If the officer revised the text while the director had it open, the echo no
//     longer matches and the backend refuses — and the honest thing to show is that the wording
//     changed, never a retry that signs whatever the text has since become.
//
// ══ SOMEONE ON THE ROSTER WHO ISN'T ASKED TO SIGN (CBG-YR3-ROLES layer 1, D-CBG-21) ══════════════
//
// Everyone serving on the board reads its consent once it has gone out — an officer who isn't a
// director, or a director added after it went out — and someone on a committee who isn't on the
// board reads only their committees' (CBG-YR6-OUTSIDE); only the people it went out to sign it. The
// read says which (`asked_to_sign`). Someone not asked reads the same page with no Sign: one quiet
// card stands where it would be, and the fingerprint's caption speaks of every signature rather than
// theirs. An answer without the field reads as asked, which is what such an answer always meant.
//
// ══ A CONSENT THAT APPROVES MINUTES (CBG-RC9) ════════════════════════════════════════════════════
//
// It says so above its text, with the way to read the minutes it names while the last signature
// landing now would approve them (each set's `would_approve`: then they are still the minutes it
// names). Once it wouldn't, the page says so before anyone signs, and why
// (`would_not_approve_reason`, the outcome the execution would record) — "already approved" is good
// news and is said quietly. The minutes open through the director record read, both parts — the
// secretary's sections and what was recorded about the meeting, the latter scoped to the closed
// sessions this director may read (CBG-FIX38) — with the fingerprint the consent names them by,
// and said unchanged only when the read's own fingerprint is that pin.

import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Fingerprint } from '../../components/fingerprint';
import { Link, useParams } from '../../board-routing';
import {
  firstErrorCode,
  isGivmoError,
  type OrgBoardDirectorAttentionOut,
  type OrgBoardDirectorConsentOut,
} from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  EmptyState,
  ErrorMessage,
  FilterBar,
  FilterChip,
  Icon,
  Skeleton,
  saasButtonVariants,
  testIdProp,
} from '@givmo/design-system';
import { FileSignature } from 'lucide-react';
import { BOARD_DIRECTOR_PAGES } from '../../board-routes';
import { useBoardDirectorOperations } from '../../board-runtime';
import {
  directorBoardIdentity,
  directorBoardSearch,
  seatCommitteeNames,
  useDirectorBoardAddress,
} from '../../director/director-board';
import { boardDirectorMeKey } from '../../director/director-me';
import { DIRECTOR_REFUSALS, isCommitteeOnly } from '../../director/director-wire';
import { committeeOnlyWords as co } from '../committees/committee-only-words';
import {
  DirectorReadRefusal,
  isSigningRefusal,
  useDirectorReadsMayFire,
} from '../../director/read-gate';
import { DirectorActRow, DirectorSetupOfferPage, useDirectorAct } from './director-act';
import { AllYourBoards } from './all-your-boards';
import { DirectorBoardContent } from './director-board-content';
import { directorPagesWords as w } from './director-pages-words';
import { DirectorReadFailed, useDirectorFailureWords } from '../../director/access-refusal';
import { DirectorShell } from './director-shell';
import { ConsentStanding } from '../consents/consent-standing';
import {
  consentStatusLabel,
  consentStatusTone,
  consentsAwaitingDirector,
  displayDate,
  type ConsentAwaitingDirector,
  displayInstant,
  extractDirectorConsents,
  isAwaitingSignatures,
  isSignatureAct,
  myCaptureLine,
  mySignatureLine,
  consentCommitteeName,
} from '../consents/consents-decode';
import { committeeMeetingsWords as cm } from '../committees/committee-meetings-words';
import {
  directorConsentMinutesSets,
  namedMinutesMatch,
  type DirectorConsentMinutes,
} from '../consents/minutes-consent-decode';
import { minutesConsentWords as rc9, minutesOfTheMeeting } from '../consents/minutes-consent-words';
import {
  closedSessionsAmong,
  minutesSetsWords as several,
  setsInASentence,
} from '../consents/minutes-sets-words';
import { closedMinutesOfTheMeeting, minutesRuleWords as rc11 } from '../minutes/minutes-rule-words';
import { useClosedMinutesOpening } from './closed-minutes-opening';
import { recordDateWording } from '../records/records-decode';
import { officerRolesWords as roles } from '../roster/officer-roles-words';
// CBG-YR4-PAY-CONSENT: the documents a consent's compensation block names.
import { directorConsentDecisions } from '../compensation/compensation-decode';
import { DirectorConsentDocuments } from './director-consent-documents';

const BACK_LINK =
  'font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline';

/**
 * What each act asks for once the sentence beside its button changed: signing's from copy round 2,
 * withdrawing's from copy round 3 (CBG-SEC2-COPY3; both D-CBG-18).
 */
const WORDS_CHANGED = {
  sign: 'The words above changed. Read them, then sign again.',
  withdraw: 'The words above changed. Read them, then withdraw again.',
} as const;

// ── The list ─────────────────────────────────────────────────────────────────────────────────────

type ConsentFilter = 'all' | 'waiting' | 'executed';

export function DirectorConsentsPage() {
  const operations = useBoardDirectorOperations();
  const { orgRef, board, notYours, scope, me, settled } = useDirectorBoardAddress();
  const mayFire = useDirectorReadsMayFire(orgRef);
  const [filter, setFilter] = useState<ConsentFilter>('all');

  const attentionQuery = useQuery({
    queryKey: ['board-director-attention', orgRef],
    enabled: orgRef !== null && mayFire,
    retry: false,
    queryFn: async (): Promise<OrgBoardDirectorAttentionOut> => {
      const { data } = await operations.directorGetBoardDirectorAttention({
        path: { org_ref: orgRef ?? '' },
        throwOnError: true,
      });
      return data;
    },
  });

  const consentsQuery = useQuery({
    queryKey: ['board-director-consents', orgRef],
    enabled: orgRef !== null && mayFire,
    retry: false,
    queryFn: async () => {
      const { data } = await operations.directorListBoardDirectorConsents({
        path: { org_ref: orgRef ?? '' },
        throwOnError: true,
      });
      return extractDirectorConsents(data);
    },
  });

  // What waits on this director is the attention read's to say, as on their to-do list; its card
  // is the consent's own, first.
  const waiting = consentsAwaitingDirector(attentionQuery.data?.items);
  const waitingIds = new Set(waiting.map((item) => item.consentId));
  const consents = consentsQuery.data ?? [];
  const listed = new Set(consents.map((consent) => consent.id));
  const ordered = [
    ...consents.filter((consent) => waitingIds.has(consent.id)),
    ...consents.filter((consent) => !waitingIds.has(consent.id)),
  ];
  // A consent the attention read names that the list doesn't (yet) hold still shows, from the item.
  const waitingOnly = waiting.filter((item) => !listed.has(item.consentId));
  const executed = consents.filter((consent) => consent.status === 'executed');
  const shown =
    filter === 'all'
      ? ordered
      : filter === 'waiting'
        ? ordered.filter((consent) => waitingIds.has(consent.id))
        : executed;
  const filters: readonly { value: ConsentFilter; label: string; count: number }[] = [
    { value: 'all', label: w.showAll, count: consents.length + waitingOnly.length },
    { value: 'waiting', label: w.showWaiting, count: waiting.length },
    { value: 'executed', label: w.showSignedByEveryone, count: executed.length },
  ];
  const search = orgRef === null ? undefined : directorBoardSearch(orgRef);

  return (
    <DirectorShell
      title={w.writtenConsents}
      description={
        // CBG-YR6-OUTSIDE (not drawn): a committee-only seat signs for its committee — said still
        // once a refusal has taken the seat away (CBG-FIX104, row 4.1: the header beside the line).
        scope.committeeOnly ? co.consentsDescription : w.consentsLine
      }
      identity={directorBoardIdentity(board)}
      actions={
        <AllYourBoards
          me={me}
          settled={settled}
          committeeOnly={scope.committeeOnly}
          testId="director-consents-back"
        />
      }
      testId="page-director-consents"
    >
      <DirectorBoardContent orgRef={orgRef} notYours={notYours}>
        {consents.length + waitingOnly.length > 0 ? (
          <FilterBar
            label="Show"
            ariaLabel="Filter written consents"
            testId="director-consents-filter"
          >
            {filters.map((option) => (
              <FilterChip
                key={option.value}
                label={option.label}
                count={option.count}
                selected={filter === option.value}
                onToggle={() => setFilter(option.value)}
                testId={`director-consents-filter-${option.value}`}
              />
            ))}
          </FilterBar>
        ) : null}

        {consentsQuery.isPending ? (
          <Skeleton className="h-84 w-full rounded-lg" testId="director-consents-loading" />
        ) : null}

        <DirectorReadRefusal
          reads={[consentsQuery, attentionQuery]}
          testId="director-consents-read-step-up"
        />

        {consentsQuery.isError && !isSigningRefusal(consentsQuery.error) ? (
          <DirectorReadFailed
            read={consentsQuery}
            line="We couldn’t load these just now."
            testId="director-consents-error"
            retryTestId="director-consents-retry"
          />
        ) : null}

        {filter !== 'executed'
          ? waitingOnly.map((item) => (
              <WaitingItemCard key={item.consentId} item={item} search={search} />
            ))
          : null}

        {shown.length > 0 ? (
          // The officer page's spacing: its cards stand in the page's column, 24px apart.
          <div className="flex flex-col gap-24" data-testid="director-consents-list">
            {shown.map((consent) => (
              <ConsentCard
                key={consent.id}
                consent={consent}
                waiting={waitingIds.has(consent.id)}
                search={search}
              />
            ))}
          </div>
        ) : null}

        {consentsQuery.isSuccess && consents.length === 0 && waiting.length === 0 ? (
          <EmptyState
            testId="director-consents-empty"
            icon={<Icon icon={FileSignature} size={20} aria-hidden />}
            title={w.noConsentsTitle}
            description={
              // CBG-FIX104 (row 4.5, approved 2026-10-17): a committee seat's own words.
              isCommitteeOnly(board)
                ? co.consentsEmpty(seatCommitteeNames(board))
                : w.noConsentsLine
            }
          />
        ) : null}
      </DirectorBoardContent>
    </DirectorShell>
  );
}

const CONSENT_TITLE =
  'font-sans text-s16 font-w600 tracking-givmo text-text-strong outline-none transition-colors hover:text-brand-700 focus-visible:underline';
const SIGN_BUTTON = saasButtonVariants({ size: 'sm', tone: 'primary', className: 'no-underline' });

/**
 * The officer's consent card (`consents-page.tsx`): the title, "Dated …" (a committee's
 * consent leading with its committee), how many of the seats it went out to have signed, and its
 * state. Under it, the director's part: "Needs your signature" with "Read and sign" while it waits on
 * them, or their own signature's line once they've given it — in the capture's own words, so an
 * officer's entry or an emailed reply never reads as a signature made in their session (plan §3.3).
 */
function ConsentCard({
  consent,
  waiting,
  search,
}: {
  consent: OrgBoardDirectorConsentOut;
  waiting: boolean;
  search: Record<string, string> | undefined;
}) {
  const required = consent.required_count ?? 0;
  const mine = consent.my_signature ?? null;
  const committee = consentCommitteeName(consent);
  return (
    <Card testId={`director-consent-${consent.id}`}>
      <CardBody className="flex flex-col gap-10">
        <div className="flex flex-wrap items-start justify-between gap-12">
          <div className="flex min-w-0 flex-col gap-4">
            <Link
              to={BOARD_DIRECTOR_PAGES.consent}
              params={{ consentId: consent.id }}
              search={search}
              className={CONSENT_TITLE}
              data-testid={`director-consent-link-${consent.id}`}
            >
              {consent.title}
            </Link>
            <span className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
              {/* CBG-YR6-CONSENT (not drawn): a committee's consent says whose it is. */}
              {committee !== null ? (
                <span className="font-w600 text-text-secondary">{cm.consentOf(committee)} · </span>
              ) : null}
              Dated {displayDate(consent.consent_date)}
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-8">
            {required > 0 ? (
              <Badge tone="neutral">
                {consent.signed_count ?? 0} of {required} signed
              </Badge>
            ) : null}
            <Badge tone={consentStatusTone(consent.status)}>
              {consentStatusLabel(consent.status)}
            </Badge>
          </div>
        </div>
        {waiting ? (
          <div
            className="flex flex-wrap items-center justify-between gap-12 border-l-2 border-border pl-12"
            data-testid={`director-consent-waiting-${consent.id}`}
          >
            <Badge tone="warning">{w.needsYourSignature}</Badge>
            <Link
              to={BOARD_DIRECTOR_PAGES.consent}
              params={{ consentId: consent.id }}
              search={search}
              className={SIGN_BUTTON}
              {...testIdProp(`director-consent-read-and-sign-${consent.id}`)}
            >
              {w.readAndSign}
            </Link>
          </div>
        ) : mine !== null && mine.occurred_at ? (
          <span
            className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
            data-testid={`director-consent-mine-${consent.id}`}
          >
            {isSignatureAct(mine) && (mine.signature_class ?? '').toLowerCase() === 'step_up'
              ? w.youSigned(displayDate(mine.occurred_at))
              : `${mySignatureLine(mine)} · ${displayDate(mine.occurred_at)}`}
          </span>
        ) : null}
      </CardBody>
    </Card>
  );
}

/** A consent the attention read says waits on this director, before the list holds it. */
function WaitingItemCard({
  item,
  search,
}: {
  item: ConsentAwaitingDirector;
  search: Record<string, string> | undefined;
}) {
  return (
    <Card testId={`director-consent-${item.consentId}`}>
      <CardBody className="flex flex-col gap-10">
        <div className="flex min-w-0 flex-col gap-4">
          <Link
            to={BOARD_DIRECTOR_PAGES.consent}
            params={{ consentId: item.consentId }}
            search={search}
            className={CONSENT_TITLE}
            data-testid={`director-consent-link-${item.consentId}`}
          >
            {item.title}
          </Link>
          <span className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
            Dated {displayDate(item.consentDate)}
          </span>
        </div>
        <div
          className="flex flex-wrap items-center justify-between gap-12 border-l-2 border-border pl-12"
          data-testid={`director-consent-waiting-${item.consentId}`}
        >
          <Badge tone="warning">{w.needsYourSignature}</Badge>
          <Link
            to={BOARD_DIRECTOR_PAGES.consent}
            params={{ consentId: item.consentId }}
            search={search}
            className={SIGN_BUTTON}
            {...testIdProp(`director-consent-read-and-sign-${item.consentId}`)}
          >
            {w.readAndSign}
          </Link>
        </div>
      </CardBody>
    </Card>
  );
}

// ── One consent ──────────────────────────────────────────────────────────────────────────────────

/**
 * One consent's page, mounted afresh for each consent (CBG-FIX137). The router keeps a route's
 * component across a change of `$consentId` alone, and the to-do list links one consent's page
 * straight to the next: kept, the next consent opened under the last one's "Thank you — your
 * signature is recorded." above its own Sign. Keyed by the consent, every line this page holds
 * for one consent — the thanks, a refusal, the wording-moved notice, the minutes reading, the
 * act in progress — belongs to that consent alone.
 */
export function DirectorConsentDetailPage() {
  const { consentId } = useParams({ from: '/director/consents/$consentId' });
  return <DirectorConsentDetail key={consentId} consentId={consentId} />;
}

function DirectorConsentDetail({ consentId }: { consentId: string }) {
  const queryClient = useQueryClient();
  const operations = useBoardDirectorOperations();
  const { orgRef, board, notYours } = useDirectorBoardAddress();
  const mayFire = useDirectorReadsMayFire(orgRef);
  // CBG-FIX104: the access refusal in the words of the seat `/me` listed here.
  const failureWords = useDirectorFailureWords();
  const [error, setError] = useState<string | null>(null);
  const [staleText, setStaleText] = useState(false);
  const [justActed, setJustActed] = useState<'signed' | 'withdrawn' | null>(null);
  const [readingMinutes, setReadingMinutes] = useState(false);

  const consentKey = ['board-director-consent', orgRef, consentId] as const;
  const consentQuery = useQuery({
    queryKey: consentKey,
    enabled: orgRef !== null && mayFire,
    // A refusal is a deterministic typed 400, not a blip: retrying it just delays the sheet.
    retry: false,
    queryFn: async () => {
      const { data } = await operations.directorGetBoardDirectorConsent({
        path: { org_ref: orgRef ?? '', consent_id: consentId },
        throwOnError: true,
      });
      return data;
    },
  });
  const consent = consentQuery.data ?? null;
  // CBG-RC9-MULTI (§M9): every set it names, in its order, each with what signing would do to it.
  const approvesSets = directorConsentMinutesSets(consent);
  // CBG-RC9 — the minutes this consent approves (its first set: the read's list alone since
  // CBG-RC9-MULTI-CONTRACT), and, while it is out, their meeting's title for the sentence that names
  // them.
  const approves = approvesSets[0] ?? null;
  // The set "Read the minutes" opens in place: the meeting's own (a closed session's open through
  // the closed-minutes opening).
  const readableSet =
    approvesSets.length > 1
      ? (approvesSets.find((set) => set.closedSession === null) ?? null)
      : approves;
  const approvesMeetingId = approves?.meetingId ?? null;
  const approvesMeetingQuery = useQuery({
    queryKey: ['board-director-meeting', orgRef, approvesMeetingId],
    enabled:
      orgRef !== null &&
      mayFire &&
      approvesMeetingId !== null &&
      isAwaitingSignatures(consent?.status ?? null),
    retry: false,
    queryFn: async () => {
      const { data } = await operations.directorGetBoardDirectorMeeting({
        path: { org_ref: orgRef ?? '', meeting_id: approvesMeetingId ?? '' },
        throwOnError: true,
      });
      return data;
    },
  });

  // CBG-RC11 (§A7, §A9): a consent naming a closed session's own minutes reads them through the
  // closed-minutes opening (CBG-RC12-READ): sent to this director, they open; else the deliberate,
  // logged step — never a read that just opens them.
  const closedOpening = useClosedMinutesOpening(orgRef, approves?.meetingId ?? '');

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: consentKey });
    await queryClient.invalidateQueries({ queryKey: ['board-director-consents', orgRef] });
    await queryClient.invalidateQueries({ queryKey: ['board-director-attention', orgRef] });
  };

  const onFailed = (caught: unknown, act: keyof typeof WORDS_CHANGED) => {
    if (isGivmoError(caught) && firstErrorCode(caught) === DIRECTOR_REFUSALS.intentChanged) {
      // The sentence beside the button changed after the page was read: read it again first.
      setError(WORDS_CHANGED[act]);
      void queryClient.invalidateQueries({ queryKey: boardDirectorMeKey() });
      return;
    }
    if (isGivmoError(caught) && caught.status === 409) {
      // The wording moved while this was open. Never sign whatever it has become.
      setStaleText(true);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: consentKey });
      return;
    }
    setError(failureWords(caught, 'We couldn’t record that. Please try again.'));
  };

  const sign = useDirectorAct('consent_signature', {
    onDone: async () => {
      await refresh();
      setJustActed('signed');
    },
    onFailed: (caught) => onFailed(caught, 'sign'),
  });
  const withdraw = useDirectorAct('consent_signature_withdrawal', {
    onDone: async () => {
      await refresh();
      setJustActed('withdrawn');
    },
    onFailed: (caught) => onFailed(caught, 'withdraw'),
  });

  const reset = () => {
    setError(null);
    setStaleText(false);
    setJustActed(null);
  };

  const beginSign = () => {
    if (consent === null || orgRef === null) return;
    reset();
    // The thunk captures this screen's echo: a confirm-and-retry resends exactly these bytes.
    const path = { org_ref: orgRef, consent_id: consentId };
    const contentHash = consent.content_hash;
    sign.begin(
      (intentStatementId) => () =>
        operations.directorSignBoardWrittenConsent({
          path,
          body: { content_hash: contentHash, intent_statement_id: intentStatementId },
          throwOnError: true,
        }),
    );
  };

  const beginWithdraw = () => {
    if (orgRef === null) return;
    reset();
    const path = { org_ref: orgRef, consent_id: consentId };
    withdraw.begin(
      (intentStatementId) => () =>
        operations.directorRevokeBoardWrittenConsentSignature({
          path,
          body: { intent_statement_id: intentStatementId },
          throwOnError: true,
        }),
    );
  };

  const identity = directorBoardIdentity(board);
  const back = (
    <Link
      to={BOARD_DIRECTOR_PAGES.consents}
      search={orgRef === null ? undefined : directorBoardSearch(orgRef)}
      // Exact: this page sits beneath that address, and it is not the current page.
      exact
      className={BACK_LINK}
      {...testIdProp('director-consent-back')}
    >
      {/* CBG-UX10: the officer consent page's way back, naming the page it opens. */}
      {w.allWrittenConsents}
    </Link>
  );

  for (const offer of [sign, withdraw]) {
    if (offer.offering) {
      return (
        <DirectorSetupOfferPage
          variant="from-sign"
          identity={identity}
          onSettled={offer.settleOffer}
        />
      );
    }
  }

  if (
    orgRef === null ||
    notYours ||
    consentQuery.isPending ||
    consentQuery.isError ||
    consent === null
  ) {
    return (
      <DirectorShell
        title="Written consent"
        identity={identity}
        // CBG-FIX34 (review-134's M1): "All written consents" names this board (`?org=`), so a board
        // this account no longer holds offers no way back into it.
        actions={notYours ? undefined : back}
        testId="page-director-consent-detail"
      >
        <DirectorBoardContent orgRef={orgRef} notYours={notYours}>
          {consentQuery.isPending ? (
            <Skeleton className="h-90 w-full rounded-lg" testId="director-consent-loading" />
          ) : null}
          {/* ⚠ A refusal must NOT land in the error card below: the read gate opens the sheet and
              retries THIS read once the window is open. */}
          <DirectorReadRefusal reads={[consentQuery]} testId="director-consent-read-step-up" />
          {consentQuery.isError && !isSigningRefusal(consentQuery.error) ? (
            <DirectorReadFailed
              read={consentQuery}
              line="We couldn’t open this."
              testId="director-consent-error"
              retryTestId="director-consent-retry"
            />
          ) : null}
        </DirectorBoardContent>
      </DirectorShell>
    );
  }

  const mySignature = consent.my_signature ?? null;
  const iHaveSigned = mySignature !== null && isSignatureAct(mySignature);
  const stillOpen = isAwaitingSignatures(consent.status);
  const capture = mySignature === null ? null : myCaptureLine(mySignature);
  // Not asked: on the roster, and not among those the consent went out to (CBG-YR3-ROLES).
  const notAsked = consent.asked_to_sign === false;

  return (
    <DirectorShell
      title={consent.title}
      description={
        consentCommitteeName(consent) !== null
          ? `${cm.consentOf(consentCommitteeName(consent) ?? '')} · Dated ${displayDate(consent.consent_date)}`
          : `Dated ${displayDate(consent.consent_date)}`
      }
      identity={identity}
      actions={back}
      testId="page-director-consent-detail"
    >
      {/* The count stated ONCE: the backend's sentence carries it, verbatim (D-CBG-18). */}
      <ConsentStanding
        statement={consent.standing_statement}
        isAnActOfTheBoard={consent.is_an_act_of_the_board === true}
        status={consent.status}
        signedCount={consent.signed_count}
        requiredCount={consent.required_count}
        testId="director-consent-standing"
      />

      {approvesSets.length > 1 && stillOpen ? (
        <ApprovesSeveralSetsCard
          sets={approvesSets}
          minutes={setsInASentence(
            approvesSets,
            { title: approvesMeetingQuery.data?.title, date: approvesSets[0]?.meetingDate },
            { keepDateTogether: true },
          )}
          onRead={() => setReadingMinutes(true)}
          onReadClosed={
            approves?.meetingId !== null && approves?.meetingId !== undefined
              ? (segmentId) => void closedOpening.ask(segmentId)
              : null
          }
          readingClosed={closedOpening.prompting ? null : closedOpening.asking}
        />
      ) : approves !== null && stillOpen ? (
        <ApprovesMinutesCard
          approves={approves}
          minutes={
            approves.closedSession === null
              ? minutesOfTheMeeting(approvesMeetingQuery.data?.title, approves.meetingDate, {
                  keepDateTogether: true,
                })
              : closedMinutesOfTheMeeting(
                  approvesMeetingQuery.data?.title,
                  approves.meetingDate,
                  approves.closedSession.purposeLabel,
                  { keepDateTogether: true },
                )
          }
          onRead={() => setReadingMinutes(true)}
          onReadClosed={
            approves.closedSession !== null && approves.meetingId !== null
              ? () => void closedOpening.ask(approves.closedSession?.segmentId ?? '')
              : null
          }
          readingClosed={closedOpening.asking !== null && !closedOpening.prompting}
        />
      ) : null}

      <Card testId="director-consent-text">
        <CardBody className="flex flex-col gap-12">
          {/* A 64-character fingerprint has no break, so the text wraps anywhere rather than run
              past its card on a phone. */}
          <pre
            className="whitespace-pre-wrap [overflow-wrap:anywhere] font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-secondary"
            data-testid="director-consent-body"
          >
            {consent.body}
          </pre>
          <div className="flex flex-col gap-6">
            <span className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
              {/* Someone not asked to sign has no signature of their own to name the wording. */}
              Version {consent.version_number} ·{' '}
              {notAsked
                ? roles.everySignatureNames
                : 'your signature names this exact wording, by its fingerprint'}
            </span>
            {/* The WHOLE fingerprint (CBG-UX4): a shortened one cannot be compared. */}
            <Fingerprint value={consent.content_hash} testId="director-consent-hash" />
          </div>
        </CardBody>
      </Card>

      {/* CBG-YR4-PAY-CONSENT: the files the text's compensation block names, each opened as Board
          fingerprinted it (nothing draws for a consent without one). */}
      {orgRef !== null ? (
        <DirectorConsentDocuments
          orgRef={orgRef}
          consentId={consentId}
          committee={Boolean(consent.committee_id)}
          decisions={directorConsentDecisions(consent)}
        />
      ) : null}

      {mySignature !== null ? (
        <Card testId="director-consent-my-standing">
          <CardBody className="flex flex-col gap-6">
            <span className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              {/* Worded by how the act was captured (CBG-UX4): "You signed this" only for an act
                  the director performed in their own session, never for an officer's entry. */}
              {mySignatureLine(mySignature)}
            </span>
            <span
              className="font-sans text-s13 font-w400 tracking-givmo text-text-tertiary"
              {...testIdProp('director-consent-my-capture')}
            >
              {/* What the act recorded: when, and how the factor was confirmed (contract §8.2). */}
              {displayInstant(mySignature.occurred_at)}
              {capture !== null ? ` · ${capture}` : ''}
            </span>
          </CardBody>
        </Card>
      ) : null}

      {justActed !== null ? (
        <p
          className="font-sans text-s14 font-w600 leading-h15 tracking-givmo text-text-strong"
          {...testIdProp('director-consent-acted')}
        >
          {justActed === 'signed'
            ? 'Thank you — your signature is recorded.'
            : 'Your signature has been withdrawn. That is recorded too.'}
        </p>
      ) : null}

      {staleText ? (
        <ErrorMessage testId="director-consent-stale">
          The wording changed while you had this open, so we haven’t recorded anything against the
          old text. Read it again above — what you see now is the current wording.
        </ErrorMessage>
      ) : null}

      {stillOpen && notAsked ? (
        // Where Sign would be: why there is none, once, quietly.
        <Card testId="director-consent-not-asked">
          <CardBody className="flex flex-col gap-4">
            <span className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              {roles.signedByDirectors}
            </span>
            <span className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {roles.notAskedToSign}
            </span>
          </CardBody>
        </Card>
      ) : null}

      {stillOpen && !notAsked ? (
        <Card testId="director-consent-actions">
          <CardBody className="flex flex-col gap-12">
            {iHaveSigned ? (
              <>
                <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
                  {/* CBG-FIX90 (not drawn): a committee's consent is the committee's to sign and to
                      meet on. */}
                  {consentCommitteeName(consent) !== null
                    ? co.withdrawLine
                    : 'You can take your signature back while your board is still signing. It doesn’t erase anything — it’s recorded as its own act, and it means the board has to meet on this instead.'}
                </p>
                <DirectorActRow
                  state={withdraw}
                  label="Take my signature back"
                  tone="secondary"
                  onPress={beginWithdraw}
                  error={error}
                  testIdPrefix="director-consent"
                  buttonTestId="director-consent-withdraw"
                />
              </>
            ) : (
              <DirectorActRow
                state={sign}
                label="Sign"
                onPress={beginSign}
                error={error}
                testIdPrefix="director-consent"
                buttonTestId="director-consent-sign"
              />
            )}
          </CardBody>
        </Card>
      ) : null}

      {readingMinutes && readableSet !== null && orgRef !== null ? (
        <NamedMinutesDialog
          orgRef={orgRef}
          approves={readableSet}
          onClose={() => setReadingMinutes(false)}
          onMinutesChanged={() => void queryClient.invalidateQueries({ queryKey: consentKey })}
        />
      ) : null}

      {sign.dialog}
      {withdraw.dialog}
      {closedOpening.dialog}
    </DirectorShell>
  );
}

/**
 * What signing does to the minutes a consent names (CBG-RC9), shown while it is out: the way to read
 * them while the last signature would still approve them, and otherwise why it won't. Never a
 * sentence the read cannot stand behind: with no answer either way (`wouldApprove` null), only the
 * lead.
 */
function ApprovesMinutesCard({
  approves,
  minutes,
  onRead,
  onReadClosed,
  readingClosed,
}: {
  approves: DirectorConsentMinutes;
  minutes: string;
  onRead: () => void;
  /** CBG-RC11: a closed session's own minutes open through the closed-minutes opening. */
  onReadClosed: (() => void) | null;
  readingClosed: boolean;
}) {
  const quiet = approves.wouldNotApproveReason === 'minutes_already_approved';
  const closed = approves.closedSession !== null;
  // CBG-RC11 (frames d4, d4b): what approving minutes is — and, to a signer the minutes record as
  // recused or as having stepped out, that it approves the record of it, not the decisions.
  const signing =
    approves.recordsYourConflict === 'recused'
      ? { testId: 'director-consent-your-recusal', text: rc11.directorYourRecusal }
      : approves.recordsYourConflict === 'stepped_out'
        ? { testId: 'director-consent-your-step-out', text: rc11.directorYourStepOut }
        : {
            testId: 'director-consent-record-not-decisions',
            text: rc11.directorRecordNotDecisions,
          };
  return (
    <Card testId="director-consent-approves">
      <CardBody className="flex flex-col gap-12">
        <div className="flex min-w-0 flex-col gap-4">
          <span className="font-sans text-s15 font-w600 leading-h15 tracking-givmo text-text-strong">
            {rc9.directorLead(minutes)}
          </span>
          {approves.wouldApprove !== false ? (
            <span
              className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
              data-testid={signing.testId}
            >
              {signing.text}
            </span>
          ) : null}
          {approves.wouldApprove === false ? (
            <span
              className={
                quiet
                  ? 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary'
                  : 'font-sans text-s14 font-w600 leading-h15 tracking-givmo text-warning-700'
              }
              data-testid="director-consent-wont-approve"
            >
              {rc9.wontApprove(approves.wouldNotApproveReason)}
            </span>
          ) : null}
        </div>
        {approves.wouldApprove === true && !closed ? (
          <Button
            appearance="saas"
            tone="secondary"
            onClick={onRead}
            className="self-start"
            testId="director-consent-read-minutes"
          >
            {rc9.readMinutes}
          </Button>
        ) : null}
        {approves.wouldApprove === true && closed && onReadClosed !== null ? (
          <Button
            appearance="saas"
            tone="secondary"
            onClick={onReadClosed}
            loading={readingClosed}
            className="self-start"
            testId="director-consent-read-closed-minutes"
          >
            {rc11.directorReadClosedMinutes}
          </Button>
        ) : null}
      </CardBody>
    </Card>
  );
}

/**
 * CBG-RC9-MULTI (frames d4, d4b): what signing does to each set a consent names, shown while it is out.
 * One lead names every set; one line says what approving minutes is — to a signer the sets record as
 * recused, that line (it wins over a step-out, the orchestrator's C11), else as having stepped out,
 * else the general one — while any set would still be approved; each set the last signature would not
 * approve says why, led by that set; and each set that would be approved has its way to be read.
 */
function ApprovesSeveralSetsCard({
  sets,
  minutes,
  onRead,
  onReadClosed,
  readingClosed,
}: {
  sets: readonly DirectorConsentMinutes[];
  minutes: string;
  onRead: () => void;
  /** A closed session's own minutes open through the closed-minutes opening, by its session. */
  onReadClosed: ((segmentId: string) => void) | null;
  /** The session whose minutes are being opened now, if any. */
  readingClosed: string | null;
}) {
  const closedSets = closedSessionsAmong(sets);
  const conflict = sets.some((set) => set.recordsYourConflict === 'recused')
    ? 'recused'
    : sets.some((set) => set.recordsYourConflict === 'stepped_out')
      ? 'stepped_out'
      : null;
  const signing =
    conflict === 'recused'
      ? { testId: 'director-consent-your-recusal', text: rc11.directorYourRecusal }
      : conflict === 'stepped_out'
        ? { testId: 'director-consent-your-step-out', text: rc11.directorYourStepOut }
        : {
            testId: 'director-consent-record-not-decisions',
            text: rc11.directorRecordNotDecisions,
          };
  const anyWould = sets.some((set) => set.wouldApprove !== false);
  return (
    <Card testId="director-consent-approves">
      <CardBody className="flex flex-col gap-12">
        <div className="flex min-w-0 flex-col gap-4">
          <span className="font-sans text-s15 font-w600 leading-h15 tracking-givmo text-text-strong">
            {rc9.directorLead(minutes)}
          </span>
          {anyWould ? (
            <span
              className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
              data-testid={signing.testId}
            >
              {signing.text}
            </span>
          ) : null}
          {sets.map((set, index) =>
            set.wouldApprove === false ? (
              <span
                key={set.recordId}
                className={
                  set.wouldNotApproveReason === 'minutes_already_approved'
                    ? 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary'
                    : 'font-sans text-s14 font-w600 leading-h15 tracking-givmo text-warning-700'
                }
                data-testid={`director-consent-wont-approve-${index}`}
              >
                {several.wontApprove(set, sets, set.wouldNotApproveReason)}
              </span>
            ) : null,
          )}
        </div>
        <div className="flex flex-wrap gap-8">
          {sets.map((set, index) => {
            if (set.wouldApprove !== true) return null;
            const session = set.closedSession;
            if (session === null) {
              return (
                <Button
                  key={set.recordId}
                  appearance="saas"
                  tone="secondary"
                  onClick={onRead}
                  testId="director-consent-read-minutes"
                >
                  {rc9.readMinutes}
                </Button>
              );
            }
            if (onReadClosed === null) return null;
            return (
              <Button
                key={set.recordId}
                appearance="saas"
                tone="secondary"
                onClick={() => onReadClosed(session.segmentId)}
                loading={readingClosed === session.segmentId}
                testId={`director-consent-read-closed-minutes-${index}`}
              >
                {closedSets > 1 ? several.readClosedMinutesOf(set) : rc11.directorReadClosedMinutes}
              </Button>
            );
          })}
        </div>
      </CardBody>
    </Card>
  );
}

/**
 * The minutes a consent names, read through the director record read (contract §15 C6; CBG-FIX38):
 * everything their fingerprint covers that a reader can read — title, date, the secretary's sections
 * and what was recorded about the meeting — with the fingerprint the consent names them by.
 *
 * The note that they haven't changed is said only when the read's own fingerprint is the consent's
 * pin (`namedMinutesMatch`). The page offered this on an earlier consent read, and the minutes stay
 * editable while the consent is out (Q8), so the bytes are checked here, read fresh on every open.
 * When they moved, the dialog says so in place of the minutes and the page asks the consent read
 * again, so its card says why signing won't approve them. A read with no fingerprint to compare
 * shows the minutes and claims nothing.
 */
function NamedMinutesDialog({
  orgRef,
  approves,
  onClose,
  onMinutesChanged,
}: {
  orgRef: string;
  approves: DirectorConsentMinutes;
  onClose: () => void;
  onMinutesChanged: () => void;
}) {
  const operations = useBoardDirectorOperations();
  const recordQuery = useQuery({
    queryKey: ['board-director-record', orgRef, approves.recordId],
    // The minutes as they stand NOW: what this dialog says about them is checked against the pin on
    // every open, never against a copy read earlier.
    staleTime: 0,
    retry: false,
    queryFn: async () => {
      const { data } = await operations.directorGetBoardDirectorRecord({
        path: { org_ref: orgRef, record_id: approves.recordId },
        throwOnError: true,
      });
      return data;
    },
  });
  const record = recordQuery.data ?? null;
  const structured = (record?.structured_record ?? '').trim();
  const match = record === null ? null : namedMinutesMatch(record, approves.contentHash);
  // Asked once per open: the consent read it refreshes re-renders this page.
  const askedAgain = useRef(false);
  useEffect(() => {
    if (match !== 'changed' || askedAgain.current) return;
    askedAgain.current = true;
    onMinutesChanged();
  }, [match, onMinutesChanged]);
  return (
    <Dialog.Root open onOpenChange={(next) => !next && onClose()}>
      <Dialog.Content
        aria-label={record?.title ?? approves.minutesTitle ?? rc9.readMinutes}
        className="flex max-h-[80vh] flex-col gap-14 overflow-y-auto"
        testId="director-consent-minutes-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 leading-h15 tracking-givmo text-text-strong">
          {record?.title ?? approves.minutesTitle ?? rc9.readMinutes}
        </Dialog.Title>
        {match === 'same' ? (
          <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
            {rc9.sheetNote}
          </p>
        ) : null}
        {match === 'changed' ? (
          <p
            className="font-sans text-s14 font-w600 leading-h15 tracking-givmo text-warning-700"
            data-testid="director-consent-minutes-changed"
          >
            {rc9.wontApprove('minutes_changed')}
          </p>
        ) : null}

        {recordQuery.isPending ? (
          <Skeleton className="h-84 w-full rounded-lg" testId="director-consent-minutes-loading" />
        ) : null}
        {recordQuery.isError ? (
          <ErrorMessage testId="director-consent-minutes-error">
            {rc9.minutesUnavailable}
          </ErrorMessage>
        ) : null}

        {record !== null && match !== 'changed' ? (
          <>
            {record.occurred_on ? (
              // The date the fingerprint covers, said as the dialog's other parts are.
              <div className="flex flex-col gap-4" data-testid="director-consent-minutes-date">
                <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
                  {recordDateWording('minutes').label}
                </span>
                <span className="font-sans text-s14 font-w400 tracking-givmo text-text-strong">
                  {displayDate(record.occurred_on)}
                </span>
              </div>
            ) : null}
            {record.body.trim().length > 0 ? (
              <div className="flex flex-col gap-4" data-testid="director-consent-minutes-body">
                <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
                  {rc9.secretarysSections}
                </span>
                <p className="whitespace-pre-wrap font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-strong">
                  {record.body}
                </p>
              </div>
            ) : null}
            {structured.length > 0 ? (
              <div
                className="flex flex-col gap-4"
                data-testid="director-consent-minutes-structured"
              >
                <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
                  {rc9.whatWasRecorded}
                </span>
                {/* The backend's own rendering, verbatim (CBG-FIX38): the officer's renderer. */}
                <p className="whitespace-pre-wrap font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
                  {record.structured_record}
                </p>
              </div>
            ) : null}
          </>
        ) : null}

        {approves.contentHash !== null && match !== 'changed' ? (
          <div className="flex flex-col gap-6">
            <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
              {rc9.theirFingerprint}
            </span>
            <Fingerprint value={approves.contentHash} testId="director-consent-minutes-hash" />
          </div>
        ) : null}

        <div className="flex justify-end">
          <Button
            appearance="saas"
            tone="secondary"
            onClick={onClose}
            testId="director-consent-minutes-close"
          >
            {rc9.close}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
